import { AudioRecorder } from '../../lib/audio-recorder';

export interface VoiceRecorderBackend {
  start(deviceId?: string): Promise<void>;
  stop(): Promise<void>;
}

export interface VoiceRecorderBackendOptions {
  sampleRateHz: number;
  onChunk: (dataBase64: string) => void;
  onLevel: (level: number) => void;
  onSpectrum?: (spectrum: number[]) => void;
}

interface VoiceRecorderOptions extends VoiceRecorderBackendOptions {
  createBackend?: (
    options: VoiceRecorderBackendOptions,
  ) => VoiceRecorderBackend;
}

const clampLevel = (level: number): number =>
  Number.isFinite(level) ? Math.max(0, Math.min(1, level)) : 0;

const isInvalidDeviceError = (error: unknown): boolean =>
  error instanceof DOMException &&
  ['NotFoundError', 'OverconstrainedError'].includes(error.name);

export class VoiceRecorder {
  private readonly backend: VoiceRecorderBackend;

  constructor(options: VoiceRecorderOptions) {
    const backendOptions: VoiceRecorderBackendOptions = {
      sampleRateHz: options.sampleRateHz,
      onChunk: options.onChunk,
      onLevel: (level) => options.onLevel(clampLevel(level)),
      onSpectrum: (spectrum) => options.onSpectrum?.(spectrum.map(clampLevel)),
    };
    this.backend = (options.createBackend ?? createAudioRecorderBackend)(
      backendOptions,
    );
  }

  async start(deviceId?: string): Promise<void> {
    try {
      await this.backend.start(deviceId || undefined);
    } catch (error: unknown) {
      if (!deviceId || !isInvalidDeviceError(error)) throw error;
      await this.backend.stop();
      await this.backend.start();
    }
  }

  async stop(): Promise<void> {
    await this.backend.stop();
  }
}

function createAudioRecorderBackend(
  options: VoiceRecorderBackendOptions,
): VoiceRecorderBackend {
  const recorder = new AudioRecorder({
    sampleRateHz: options.sampleRateHz,
    onData: options.onChunk,
    onVolume: options.onLevel,
    onSpectrum: options.onSpectrum,
  });
  return {
    start: (deviceId?: string) => recorder.start(deviceId),
    stop: () => recorder.stop(),
  };
}
